import React, { useEffect, forwardRef, useRef } from 'react';

interface Skill {
  name: string;
  percentage: number;
}

const About: React.ForwardRefRenderFunction<HTMLElement> = (props, ref) => {
  const skills: Skill[] = [
    { name: 'React.js', percentage: 95 },
    { name: 'Next.js', percentage: 90 },
    { name: 'ASP.NET Web API', percentage: 85 },
    { name: 'JavaScript/TypeScript', percentage: 95 },
    { name: 'C#', percentage: 85 },
    { name: 'HTML/CSS', percentage: 90 },
    { name: 'SQL/NoSQL', percentage: 80 },
    { name: 'Git/DevOps', percentage: 75 }
  ];

  const skillBarsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            skillBarsRef.current.forEach((bar, index) => {
              setTimeout(() => {
                if (bar) {
                  bar.style.width = `${skills[index].percentage}%`;
                }
              }, 100 * index);
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    const currentRef = (ref as React.RefObject<HTMLElement>)?.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, skills]);

  return (
    <section id="about" className="section-padding" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-white md:text-4xl font-bold section-title text-center">About Me</h2>
          <p className="text-white mt-4 max-w-3xl mx-auto">
            Here you'll find more information about me, my current skills, and my experience as a fullstack developer.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="section-reveal" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-white font-bold mb-4 text-secondary">Get to know me!</h3>
            <div className="space-y-4 text-white">
              <p>
                I'm a <strong>Fullstack Developer</strong> specializing in building exceptional digital experiences that are fast, accessible, and responsive. I focus on creating clean, efficient code that solves real business problems.
              </p>
              <p>
                With over 4 years of experience in web development, I've worked on a diverse range of projects from small business websites to complex enterprise applications. My expertise spans both frontend and backend technologies, with a particular focus on React.js, Next.js, and ASP.NET Web API.
              </p>
              <p>
                I'm passionate about staying current with the latest industry trends and continuously expanding my skillset. 
              </p>
              <p>
                I'm open to job opportunities where I can contribute, learn, and grow. If you have a project that matches my skills and experience, don't hesitate to contact me.
              </p>
            </div>
            <div className="mt-6">
              <a
                href="#contact"
                className="inline-block bg-primary hover:bg-blue-600 text-white py-3 px-8 rounded-full transition duration-300 font-semibold"
              >
                Contact Me
              </a>
            </div>
          </div>
          
          <div className="section-reveal" style={{ animationDelay: '0.4s' }}>
            <h3 className="text-white font-bold mb-4 text-secondary">My Skills</h3>
            <div className="space-y-2">
              {skills.map((skill, index) => (
                <div key={index} className="mb-5">
                  <div className="flex justify-between mb-1">
                    <span className="font-medium text-white">{skill.name}</span>
                    <span className="text-primary font-medium">{skill.percentage}%</span>
                  </div>
                  <div className="skill-bar">
                    <div 
                      className="skill-progress" 
                      style={{ width: '0%' }}
                      ref={(el) => {
                        if (el) skillBarsRef.current[index] = el;
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            
            <h3 className="text-white font-bold mt-10 mb-4 text-secondary">Education & Certification</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-primary pl-4 py-2">
                <p className="text-sm text-white">2013 - 2018</p>
                <h4 className="text-lg text-white font-bold">BS in Electronics Engineering</h4>
                <p className="text-white">Capital University of Science and Technology, Islamabad</p>
              </div>
              <div className="border-l-4 border-primary pl-4 py-2">
                <p className="text-sm text-white">2020</p>
                <h4 className="text-lg font-bold text-white">Microsoft Certified: ASP .NET Web Developer</h4>
                <p className="text-white">Microsoft</p>
              </div>
              <div className="border-l-4 border-primary pl-4 py-2">
                <p className="text-sm text-white">2021</p>
                <h4 className="text-lg font-bold text-white">React.js & Next.js Certification</h4>
                <p className="text-white">Frontend Masters</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default forwardRef(About);